// src/api/staff.ts
import api from "./client";
import { fetchServices as fetchServicesApi } from "./services";

export type ServiceSummary = {
  service_id: string;
  service_name: string;
};

export type CalendarSummary = {
  id: string;
  display_name: string;
  is_bookable: boolean;
};

export type Staff = {
  staff_id: string;
  full_name: string;
  bio?: string | null;
  profile_photo_url?: string | null;
  is_active: boolean;
  calendar_id?: string | null;
  calendar_name?: string | null;
  calendar_is_bookable?: boolean;
  service_ids?: string[];
  services?: ServiceSummary[];
};

export type StaffPayload = {
  full_name: string;
  bio?: string | null;
  profile_photo_url?: string | null;
  is_active: boolean;
  calendar_id?: string | null;
  service_ids?: string[];
};

// 📥 GET all staff
export const fetchStaff = async (): Promise<Staff[]> => {
  try {
    const res = await api.get("/manageBusinessStaff");
    return res.data || [];
  } catch (error) {
    console.error("🚨 Failed to fetch staff:", error);
    throw error;
  }
};

// ➕ POST create staff
export const createStaff = async (payload: StaffPayload): Promise<Staff> => {
  try {
    const res = await api.post("/manageBusinessStaff", payload);
    return res.data;
  } catch (error) {
    console.error("🚨 Failed to create staff:", error);
    throw error;
  }
};

// ✏️ PUT update staff
export const updateStaff = async (
  staffId: string,
  payload: StaffPayload
): Promise<Staff> => {
  try {
    const body = { staff_id: staffId, ...payload };
    const res = await api.put("/manageBusinessStaff", body);
    return res.data;
  } catch (error: any) {
    const backendError = error?.response?.data;
    const message =
      backendError?.error ||
      backendError?.message ||
      error.message ||
      "Failed to update staff";

    console.error("🚨 Staff update failed:", backendError || error);
    throw new Error(message);
  }
};

// 🗑️ DELETE staff
export const deleteStaff = async (staffId: string): Promise<void> => {
  try {
    await api.delete("/manageBusinessStaff", {
      data: { staff_id: staffId },
    });
  } catch (error) {
    console.error("🚨 Failed to delete staff:", error);
    throw error;
  }
};

// 🔧 Fetch all business calendars (for StaffModal)
export const fetchCalendars = async (): Promise<CalendarSummary[]> => {
  try {
    const response = await api.get("/business_calendars");

    // ⭐ Always return an array
    const rows = Array.isArray(response.data) ? response.data : [];

    return rows.map((row: any) => ({
      id: row.business_calendar_id,
      display_name: row.calendar_name,
      is_bookable: !!row.is_bookable,
    }));
  } catch (error) {
    console.error("🚨 Failed to fetch calendars:", error);
    return [];
  }
};

// � POST upload staff photo
export const uploadStaffPhoto = async (
  staffId: string,
  file: File
): Promise<string> => {
  const res = await api.post<{ photo_url: string }>(
    `/staff/${staffId}/photo`,
    file,
    {
      headers: {
        "Content-Type": file.type,
        "X-File-Name": file.name,
      },
    }
  );
  return res.data.photo_url;
};

// 🗑️ DELETE staff photo
export const deleteStaffPhoto = async (staffId: string): Promise<void> => {
  await api.delete(`/staff/${staffId}/photo`);
};

// �🔁 Reuse services API
export const fetchServices = fetchServicesApi;
